import type { AnonymousFixture } from "./types";

export type DraftSource = "property" | "code" | "component";
export type DraftOperation = "replace" | "clear" | "remove";

export interface DraftEntry {
  readonly elementId: string;
  readonly property: string;
  readonly before: unknown;
  readonly after: unknown;
  readonly operation: DraftOperation;
  readonly source: DraftSource;
  readonly outcome: "proposed";
  readonly diff: { readonly path: string; readonly before: unknown; readonly after: unknown };
}

export interface DraftLedger {
  readonly schemaVersion: "workbench-local-draft/1";
  readonly fixtureId: string;
  readonly entries: readonly DraftEntry[];
  readonly validation: {
    readonly state: "partial";
    readonly reason: string;
    readonly assessedElementCount: number;
  };
}

export interface BulkPropertyDraftResult {
  readonly ledger: DraftLedger;
  readonly selected: number;
  readonly staged: number;
  readonly alreadyMissing: number;
  readonly refused: number;
  readonly operation: DraftOperation;
}

const geometryProperties = new Set(["height", "width", "length", "area", "volume", "geometry", "thickness"]);

export function emptyDraftLedger(fixtureId: string): DraftLedger {
  return {
    schemaVersion: "workbench-local-draft/1",
    fixtureId,
    entries: [],
    validation: {
      state: "partial",
      reason: "Ephemeral Workbench draft only; no authoritative validation or commit was requested.",
      assessedElementCount: 0
    }
  };
}

function ledgerWithEntries(ledger: DraftLedger, entries: readonly DraftEntry[]): DraftLedger {
  const sorted = [...entries].sort(
    (left, right) => left.elementId.localeCompare(right.elementId) || left.property.localeCompare(right.property)
  );
  return {
    ...ledger,
    entries: sorted,
    validation: { ...ledger.validation, assessedElementCount: new Set(sorted.map((entry) => entry.elementId)).size }
  };
}

export interface StageDraftChangeResult {
  readonly ledger: DraftLedger;
  readonly selected: number;
  readonly staged: number;
  readonly refused: number;
}

/**
 * Selection can freely mix editable and read-only/unsupported elements (Skipped diagnostic records,
 * geometry-invalid boxes, ...) -- inspecting a read-only element is how kaz found a real property
 * name (`TypeId`) to edit in the first place, so selection itself is never restricted. What used to
 * be wrong was staging: a selection with even one read-only element refused the *entire* batch,
 * discarding every editable element in it too. This now matches `stageBulkPropertyChange`'s existing
 * behavior exactly: skip and count refused elements, stage the rest, and only throw when the whole
 * selection produced nothing to stage (kaz, 2026-08-11 -- found from a real screenshot of exactly this
 * refusal on a single Skipped-record selection).
 */
export function stageDraftChange(
  fixture: AnonymousFixture,
  ledger: DraftLedger,
  selectedElementIds: ReadonlySet<string>,
  property: string,
  value: string,
  source: DraftSource
): StageDraftChangeResult {
  const normalizedProperty = property.trim();
  if (ledger.fixtureId !== fixture.id) throw new RangeError("Draft ledger belongs to a different inspection dataset.");
  if (selectedElementIds.size === 0) throw new RangeError("Select at least one element before staging a draft change.");
  if (normalizedProperty === "" || value.trim() === "") throw new RangeError("A draft change requires a non-empty property and value.");
  if (geometryProperties.has(normalizedProperty.toLowerCase())) {
    throw new RangeError("Geometry-affecting properties cannot be staged in the Workbench draft.");
  }

  const elements = new Map(fixture.elements.map((element) => [element.id, element]));
  const unknown = [...selectedElementIds].filter((id) => !elements.has(id)).sort();
  if (unknown.length > 0) throw new RangeError(`Selected element is not in the inspection dataset: ${unknown.join(", ")}`);

  const next = new Map(ledger.entries.map((entry) => [`${entry.elementId}\u0000${entry.property}`, entry]));
  let staged = 0;
  let refused = 0;
  for (const elementId of [...selectedElementIds].sort()) {
    const element = elements.get(elementId)!;
    if (!element.editable || element.unsupportedReason !== undefined) {
      refused += 1;
      continue;
    }
    const key = `${elementId}\u0000${normalizedProperty}`;
    const existing = next.get(key);
    const before =
      existing?.before ??
      (Object.prototype.hasOwnProperty.call(element.properties, normalizedProperty)
        ? structuredClone(element.properties[normalizedProperty])
        : { state: "missing" });
    next.set(key, {
      elementId,
      property: normalizedProperty,
      before,
      after: value,
      operation: "replace",
      source,
      outcome: "proposed",
      diff: { path: `/properties/${normalizedProperty}`, before: structuredClone(before), after: value }
    });
    staged += 1;
  }
  if (staged === 0) {
    throw new RangeError(
      refused === selectedElementIds.size
        ? "Every selected element is read-only or unsupported."
        : `Selected elements produced nothing to stage for ${normalizedProperty}.`
    );
  }
  return { ledger: ledgerWithEntries(ledger, [...next.values()]), selected: selectedElementIds.size, staged, refused };
}

export function stageBulkPropertyChange(
  fixture: AnonymousFixture,
  ledger: DraftLedger,
  selectedElementIds: ReadonlySet<string>,
  property: string,
  operation: DraftOperation,
  value = ""
): BulkPropertyDraftResult {
  const normalizedProperty = property.trim();
  if (ledger.fixtureId !== fixture.id) throw new RangeError("Draft ledger belongs to a different inspection dataset.");
  if (selectedElementIds.size === 0) throw new RangeError("Select at least one element before staging a draft change.");
  if (normalizedProperty === "") throw new RangeError("A bulk property change requires a non-empty property.");
  if (operation === "replace" && value.trim() === "") throw new RangeError("Replace all requires a non-empty value.");
  if (geometryProperties.has(normalizedProperty.toLowerCase())) {
    throw new RangeError("Geometry-affecting properties cannot be staged in the Workbench draft.");
  }

  const elements = new Map(fixture.elements.map((element) => [element.id, element]));
  const unknown = [...selectedElementIds].filter((id) => !elements.has(id)).sort();
  if (unknown.length > 0) throw new RangeError(`Selected element is not in the inspection dataset: ${unknown.join(", ")}`);
  const next = new Map(ledger.entries.map((entry) => [`${entry.elementId}\u0000${entry.property}`, entry]));
  let staged = 0;
  let alreadyMissing = 0;
  let refused = 0;
  for (const elementId of [...selectedElementIds].sort()) {
    const element = elements.get(elementId)!;
    if (!element.editable || element.unsupportedReason !== undefined) {
      refused += 1;
      continue;
    }
    const key = `${elementId}\u0000${normalizedProperty}`;
    const existing = next.get(key);
    const hasSourceProperty = Object.prototype.hasOwnProperty.call(element.properties, normalizedProperty);
    if (operation !== "replace" && !hasSourceProperty && existing === undefined) {
      alreadyMissing += 1;
      continue;
    }
    const before = existing?.before ?? (hasSourceProperty
      ? structuredClone(element.properties[normalizedProperty])
      : { state: "missing" });
    const after: unknown = operation === "replace" ? value : operation === "clear" ? "" : { state: "removed" };
    next.set(key, {
      elementId,
      property: normalizedProperty,
      before,
      after,
      operation,
      source: "property",
      outcome: "proposed",
      diff: { path: `/properties/${normalizedProperty}`, before: structuredClone(before), after: structuredClone(after) }
    });
    staged += 1;
  }
  if (staged === 0) {
    throw new RangeError(
      refused === selectedElementIds.size
        ? "Every selected element is read-only or unsupported."
        : `Property ${normalizedProperty} is already missing from every editable selected element.`
    );
  }
  return {
    ledger: ledgerWithEntries(ledger, [...next.values()]),
    selected: selectedElementIds.size,
    staged,
    alreadyMissing,
    refused,
    operation
  };
}

export function removeDraftChange(ledger: DraftLedger, elementId: string, property: string): DraftLedger {
  return ledgerWithEntries(
    ledger,
    ledger.entries.filter((entry) => entry.elementId !== elementId || entry.property !== property)
  );
}
